import { Link, useSearchParams } from 'react-router-dom';
import { ArrowRightIcon } from 'lucide-react';
import { HeadingText } from '../shared/HeadingText';
import { MediaView } from '../shared/MediaView';
import { ParallaxMedia } from '../shared/ParallaxMedia';
import { founder, salon } from '../../data/content';
import { usePages } from '../../contexts/SiteContext';
import { useCatalog } from '../../contexts/CatalogContext';
import type { ArticleCategory } from '../../core/types/content';
import { plural } from '../../core/format/text';
import { fill } from '../../utils/copy';
import { btnPrimaryLg, chipCls } from '../../ui/styles';
import { heroTitle } from '../../ui/sections';
import { pageActions } from '../../data/pageActions';
import { articleTexture } from '../../data/placeholderImages';
import { MentionedProducts } from './MentionedProducts';
import { JournalPodcast } from './JournalPodcast';
import { articlePath, articlesIn, categoriesOf } from './journalRules';

/**
 * Het journal als tijdschrift: kop, het nieuwste artikel groot met beeld en genoemde producten,
 * de rest in een raster (beeld waar dat er is), en de podcast op klei.
 */
export function Journal() {
  const pages = usePages();
  const {
    articles,
    productsByIds
  } = useCatalog();
  const copy = pages.journal;
  const categories = categoriesOf(articles);
  // De categorie staat in het adres (?categorie=…): terug uit een artikel staat dezelfde keuze er nog.
  const [params, setParams] = useSearchParams();
  const asked = params.get('categorie');
  const category: ArticleCategory | null = categories.find(c => c === asked) ?? null;
  const setCategory = (c: ArticleCategory | null) => {
    const next = new URLSearchParams(params);
    if (c) next.set('categorie', c);else next.delete('categorie');
    setParams(next, {
      replace: true,
      preventScrollReset: true
    });
  };
  const linkState = {
    fromJournal: `/journal${params.toString() ? `?${params.toString()}` : ''}`
  };
  const [featured, ...rest] = articlesIn(articles, category);
  const featuredProducts = productsByIds(featured?.productIds ?? []);
  return <>
      <section className="bg-sand" data-id="element-7621" data-mp="36x0gb1uqphq2">
        <div className="mx-auto grid max-w-content gap-10 px-6 pb-10 pt-12 lg:grid-cols-12 lg:gap-16 lg:px-10 lg:pb-14 lg:pt-20" data-id="element-7622" data-mp="3avhlvgyiik5d">
          <div className="lg:col-span-8" data-id="element-7623" data-mp="3fvvnag5lyvuf">
            {copy.hero.eyebrow && <p className="text-[14px] text-clay" data-id="element-7624" data-mp="3kup0s8kwamul">{fill(copy.hero.eyebrow)}</p>}
            <h1 className={`mt-4 max-w-3xl lg:mt-5 ${heroTitle}`} data-id="element-7625" data-mp="2n6yf55v42ffe">
              <HeadingText heading={copy.hero.heading} data-id="element-7626" data-mp="2n4lnu23gqspo" />
            </h1>
          </div>
          <div className="lg:col-span-4 lg:self-end" data-id="element-7627" data-mp="2s6199v9jz18l">
            <div className="flex items-center gap-4 border-t border-ink/15 pt-6" data-id="element-7628" data-mp="2x4clgcnblntz">
              {/* Decoratief: haar naam staat ernaast. */}
              <img src={founder.portrait} alt="" width={56} height={56} className="h-14 w-14 shrink-0 rounded-full object-cover object-top" data-id="element-7629" data-mp="31wvucwb2busg" />
              <p className="text-[14px] leading-snug text-muted" data-id="element-7630" data-mp="29f0dfg3qblt9">
                {fill(copy.hero.byline)} <span className="text-ink" data-id="element-7631" data-mp="21vr4tp31rr59">{founder.name}</span>
                <br data-id="element-7632" data-mp="21uzh10foyxpj" />
                {founder.role} · {salon.city}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Artikelen: eerst, want hier komen lezers via Google binnen */}
      <section className="bg-sand pb-20 sm:pb-24 lg:pb-32" aria-labelledby="journal-articles" data-id="element-7633" data-mp="1rze96lnxz37v">
        <div className="mx-auto max-w-content px-6 lg:px-10" data-id="element-7634" data-mp="1n0um2hxm3ldx">
          <div className="flex flex-wrap items-center justify-between gap-4 border-t border-ink/15 pt-6" data-id="element-7635" data-mp="1ixohdeq5e29k">
            <h2 id="journal-articles" className="text-[14px] text-ink" data-id="element-7636" data-mp="1e4lnf5hgkqsu">
              {fill(copy.articlesHeading)}
            </h2>
            <div role="group" aria-label="Kies een categorie" className="-mx-6 flex min-w-0 max-w-[calc(100%+3rem)] gap-2 overflow-x-auto px-6 [scrollbar-width:none] sm:mx-0 sm:max-w-full sm:flex-wrap sm:px-0 [&::-webkit-scrollbar]:hidden" data-id="element-7637" data-mp="194qukwe4hr55">
              <button type="button" aria-pressed={category === null} onClick={() => setCategory(null)} className={`${chipCls(category === null)} shrink-0`} data-id="element-7638" data-mp="v9ohnpauz1iu">
                Alles
              </button>
              {categories.map(c => <button key={c} type="button" aria-pressed={category === c} onClick={() => setCategory(c)} className={`${chipCls(category === c)} shrink-0`} data-id="element-7639" data-mp="1b6g1yr82w49a">
                  {c}
                </button>)}
            </div>
          </div>

          {featured ? <article className="mt-10 grid gap-10 lg:grid-cols-12 lg:gap-16" aria-labelledby="featured-title" data-id="element-7640" data-mp="166a3os7azji8">
              <Link to={articlePath(featured)} state={linkState} className="block lg:col-span-7" tabIndex={-1} aria-hidden="true" data-id="element-7641" data-mp="s7k9bkbvbxxk">
                {/* Sfeerbeeld: de titel staat ernaast. Zonder eigen beeld het portret, zodat het vlak nooit leeg is.
                    Het ene moment van deze pagina: het beeld beweegt licht binnen zijn kader. */}
                <ParallaxMedia media={{
              image: featured.image || articleTexture(featured.category),
              alt: ''
            }} priority sizes="(min-width: 1024px) 55vw, 100vw" className="aspect-[4/3] w-full" data-id="element-7642" data-mp="2qkatcjyp76ru" />
              </Link>
              <div className="lg:col-span-5 lg:self-center" data-id="element-7643" data-mp="avn531vy05cf">
                <p className="text-[13px] text-clay" data-id="element-7644" data-mp="opmqbyu28ohr">
                  {featured.category} · {featured.readTime} · {featured.date}
                </p>
                <h3 id="featured-title" className="mt-3 type-sub text-ink" data-id="element-7645" data-mp="ymsilaeeuljk">
                  <Link to={articlePath(featured)} state={linkState} className="transition-colors duration-150 ease-soft hover:text-clay" data-id="element-7646" data-mp="yovjiph3pz32">
                    {featured.title}
                  </Link>
                </h3>
                <p className="mt-5 text-[17px] leading-relaxed text-muted" data-id="element-7647" data-mp="12ovxk90ag89l">{featured.excerpt}</p>
                {/* De hoofdknop van het journal: het nieuwste artikel. */}
                <Link to={articlePath(featured)} state={linkState} className={`group mt-7 ${btnPrimaryLg}`} data-id="element-7648" data-mp="39h6j367mb251">
                  {pageActions.readLatest}
                  <span className="sr-only" data-id="element-7649" data-mp="18dw6mt8qtxlw">: {featured.title}</span>
                  <ArrowRightIcon className="h-4 w-4 transition-transform duration-200 ease-soft group-hover:translate-x-0.5" strokeWidth={1.5} aria-hidden="true" data-id="element-7650" data-mp="1dbpb9hueejk2" />
                </Link>

                {featuredProducts.length > 0 && <div className="mt-9 border-t border-ink/15 pt-6" data-id="element-7651" data-mp="1mdf2amkdbghb">
                    <p className="text-[14px] text-ink" data-id="element-7652" data-mp="1rc8g6g3kh9i0">Genoemd in dit artikel</p>
                    <MentionedProducts products={featuredProducts} data-id="element-7653" data-mp="1watgefitjt6g" />
                  </div>}
              </div>
            </article> : <p className="mt-10 text-[17px] text-muted" data-id="element-7654" data-mp="212bjb94ek3u2">{fill(copy.emptyCategory)}</p>}

          {rest.length > 0 && <ul className="mt-20 grid gap-x-8 gap-y-14 sm:grid-cols-2 lg:grid-cols-3" data-id="element-7655" data-mp="2a283axfukeis">
              {rest.map(a => <li key={a.slug} className="h-full" data-id="element-7656" data-mp="2f0rm4oz59a6x">
                  <Link to={articlePath(a)} state={linkState} className="group flex h-full flex-col" data-id="element-7657" data-mp="2ez88l64vhd8u">
                    {a.image ? <MediaView media={{
                image: a.image,
                alt: ''
              }} sizes="(min-width: 1024px) 30vw, (min-width: 640px) 45vw, 100vw" className="aspect-[3/2] w-full" data-id="element-7658" data-mp="3ujr0m6xjrl7e" /> :
              // Geen beeld: een rustig vlak in dezelfde maat, zodat titels in de rij gelijk staan.
              <span aria-hidden="true" className="flex aspect-[3/2] w-full items-end bg-shell p-5" data-id="element-7659" data-mp="m4ystpwrek4w">
                        <span className="type-name italic text-clay" data-id="element-7660" data-mp="i2lqzcl4v6de">{a.category}</span>
                      </span>}
                    <p className="mt-5 text-[13px] text-clay" data-id="element-7661" data-mp="daargaub1mld">{a.category}</p>
                    <h3 className="mt-2 type-title text-ink transition-colors duration-150 ease-soft group-hover:text-clay" data-id="element-7662" data-mp="8c977ml57h8b">
                      {a.title}
                    </h3>
                    <p className="mt-3 text-[15px] leading-relaxed text-muted" data-id="element-7663" data-mp="111g3eex4juqe">{a.excerpt}</p>
                    <p className="mt-auto pt-4 text-[13px] text-muted" data-id="element-7664" data-mp="9dv939hafa1x">
                      {a.date} · {a.readTime}
                      {a.productIds && a.productIds.length > 0 && <span className="text-clay" data-id="element-7665" data-mp="e6ofe1semqg4"> · {plural(a.productIds.length, 'product', 'producten')} uit de shop</span>}
                    </p>
                  </Link>
                </li>)}
            </ul>}
        </div>
      </section>

      <JournalPodcast data-id="element-7666" data-mp="s55x9ivmp8q4" />
    </>;
}